"use client";

import React, { useEffect, useCallback, useState, useMemo, useRef } from "react";
import moment from "moment-timezone";
import { useCheckoutBase, defaultGuest } from "../hooks/use-checkout-base";
import { CheckoutPanel } from "../checkout-panel";
import { CheckoutFooter } from "../checkout-footer";
import { CheckoutSummaryCard } from "../checkout-summary-card";
import { AppointmentTimeSlotStep } from "../appointment-time-slot-step";
import { AddOnsStep } from "../add-ons-step";
import { UserFormStep } from "../user-form-step";
import { ParticipantsStep, participantsToPayload } from "../participants-step";
import type {
  CheckoutParticipantField,
  ExistingParticipant,
} from "../participants-step";
import { PersonalDetailsStep } from "../personal-details-step";
import { hasRenderableQuestions } from "../utils/guest-questions";
import {
  buildTicketDurationIndex,
  resolveAppointmentDurationMinutes,
} from "../utils/appointment-duration";
import { validateGuest, hasGuestErrors } from "../validation";
import { BasketChoiceStep, type BasketChoiceNotice } from "../basket-choice-step";
import { useV2Pricing } from "../hooks/use-v2-pricing";
import { TicketSelectionStepV2 } from "../ticket-selection-step-v2";
import {
  appointmentCapTicketTypeId,
  appointmentPoolCaps,
  appointmentPoolCapsFromSlot,
  isVariableDurationFlow,
  poolDemandInTicketsOf,
  poolSelectionsExceedCaps,
  ticketIsUnbookableOnPool,
  poolDemandUnitsOnPool,
  slotPoolFit,
} from "../../../lib/checkout/pool-availability";
import {
  formatBookingDuration,
  formatWindowEnd,
} from "../../../lib/checkout/booking-window";
import type { CustomerBookingHistory } from "../../../lib/checkout/types";
import type {
  DiscountResult,
  CheckoutStep,
  TicketSelection,
} from "../utils/types";

export type CheckoutAppointmentProps = {
  // Data
  course: any;
  ticketTypes: any[];
  ticketTypesV2?: any[];
  priorPurchasedTicketIds?: string[];
  customerBookingHistory?: CustomerBookingHistory;
  cartPricingRules?: any[];
  addOns?: any[];
  addOnGroups?: any[];
  user?: any;
  location?: any;
  unavailableTimeBlocks?: any[]; // For calendar blocking
  bundleID?: string;
  bundle?: any;

  // Callbacks
  onLoadAvailableSlots: (
    month: number,
    year: number,
    ticketTypeId?: string,
    locationId?: string,
  ) => Promise<{
    dates: any[];
    slots: any[];
    /** Name map for every effective location in the schedule, keyed by PartnerLocation id (display-only). */
    locationsById?: Record<string, { name: string }>;
    /** Distinct effective locations across the whole schedule (filter-independent) — filter dropdown options. */
    locationOptions?: Array<{ id: string; name: string }>;
    /** Whether the schedule spans 2+ distinct locations (schedule-wide, filter-independent). */
    hasMultipleLocations?: boolean;
    /** Effective "location first" mode resolved by the API ("required" gates dates behind a location choice). */
    locationSelectionMode?: "optional" | "required";
  }>;
  onConfirmFreeBooking?: (data: any) => Promise<void>;
  onValidateDiscount?: (code: string, email: string) => Promise<DiscountResult>;
  onSuccess?: (purchase: any) => void;
  onError?: (error: Error | string) => void;
  onClose?: () => void;
  onStepChange?: (step: string) => void;

  // Config
  currency?: string;
  timezone?: string;
  cancellationPolicyUrl?: string;
  /** Terms settings for dialog display */
  termsSettings?: import("../utils/types").TermsSettings;
  /** Custom terms for dialog display */
  customTerms?: import("../utils/types").CustomTerms;
  /** Callback for webcomponents to open terms dialog in external portal */
  onOpenTermsDialog?: (data: {
    termsSettings?: import("../utils/types").TermsSettings;
    customTerms?: import("../utils/types").CustomTerms;
    timezone?: string;
  }) => void;

  // School/Business settings
  school?: {
    name?: string;
    _id?: string;
    isNewsletterSignupEnabled?: boolean;
    customNewsletterSignupText?: string | null;
    businessCountry?: string;
  };
  mandatoryTickBox?: boolean;
  allowPrefillOthersGuestsOnCheckout?: boolean;
  // Participants (attendees) — threaded from the orchestrator/widget.
  participantsEnabled?: boolean;
  participantLabel?: string;
  participantFields?: CheckoutParticipantField[];
  existingParticipants?: ExistingParticipant[];
  buyerIsAttendee?: boolean;
  /** Name-later listing: the attendee step accepts empty places. */
  allowUnnamedAttendees?: boolean;
  /** The signed-in customer's identity, for naming the buyer on their own card. */
  customer?: { firstName?: string; lastName?: string; email?: string; phone?: string };
  /** Answers the buyer has already given about themselves elsewhere in this basket. */
  selfSeed?: Record<string, unknown>;
  onSelfAnswersChange?: (answers: Record<string, unknown>) => void;
  /** When true, footer is not sticky - flows after content (for iframe embeds) */
  isEmbedMode?: boolean;
  /** Custom subtitle for the checkout header (e.g., "Private Booking" for groupSchedule) */
  subtitle?: string;
  /** When true, this is a group schedule booking (private booking) with guest count selection */
  isGroupSchedule?: boolean;
  /** Callback to trigger login flow. When provided and user is not logged in, shows "Log in" prompt in payment methods. */
  onLogin?: () => void;

  // Basket feature
  /** When true, shows the bundle/membership helper (widget-only, basket-enabled). */
  showBundleMembershipHelp?: boolean;
  basketItemCount?: number;
  onAddToBasket?: (itemData: any) => void;
  /** Callback when user clicks "Proceed to Checkout" in BasketChoiceStep — adds item to basket and navigates to basket checkout page */
  onProceedToBasketCheckout: (itemData: {
    type: "booking";
    bookingType?: "regular" | "dropIn" | "trial" | "term" | "appointment";
    courseId: string;
    courseName: string;
    sessionId?: string;
    sessionDate: string;
    ticketSelections: Array<{
      ticketTypeId: string;
      ticketTypeName: string;
      quantity: number;
      price: number;
    }>;
    addOns?: Array<{
      addOnId: string;
      addOnName: string;
      quantity: number;
      price: number;
    }>;
    userInputs?: Array<{
      index: number;
      guestIndex: number | null;
      label: string;
      value: string | boolean | Date | null;
    }>;
    appointmentData?: {
      startTime: string;
      endTime: string;
      duration: number;
    };
    /** The LISTING's timezone, so every basket surface renders the window on
     *  the same clock — see formatBookingWindow. */
    timezone?: string;
    guestDetails?: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    checkoutGuestsInformation?: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      marketingOptIn?: boolean;
      mandatoryTickBox?: boolean;
    }>;
    participants?: Array<{
      id?: string;
      answers: Array<{ slug: string; value: unknown }>;
    }>;
    price: number;
    currency: string;
    imageUrl?: string;
    isOnline?: boolean;
  }) => void;
  /**
   * When true, skip the basket choice step and auto-fire onProceedToBasketCheckout.
   */
  directCheckout?: boolean;
  /** Why the host refused the last basket choice; shown on the basket choice step. */
  basketConflict?: BasketChoiceNotice | null;
};

export function CheckoutAppointment({
  course,
  ticketTypes,
  ticketTypesV2 = [],
  priorPurchasedTicketIds,
  customerBookingHistory,
  cartPricingRules = [],
  addOns = [],
  addOnGroups = [],
  user,
  onLoadAvailableSlots,
  onClose,
  onStepChange,
  currency = "GBP",
  timezone,
  cancellationPolicyUrl = "/terms",
  termsSettings,
  customTerms,
  onOpenTermsDialog,
  bundleID,
  bundle,
  mandatoryTickBox = false,
  allowPrefillOthersGuestsOnCheckout = false,
  participantsEnabled = false,
  participantLabel = "Attendees",
  participantFields = [],
  existingParticipants = [],
  buyerIsAttendee = false,
  allowUnnamedAttendees = false,
  customer,
  selfSeed,
  onSelfAnswersChange,
  isEmbedMode = false,
  school,
  subtitle,
  isGroupSchedule = false,
  basketItemCount = 0,
  onAddToBasket,
  onProceedToBasketCheckout,
  directCheckout = false,
  basketConflict = null,
  showBundleMembershipHelp = false,
}: CheckoutAppointmentProps) {
  const showParticipantsStep = participantsEnabled && participantFields.length > 0;

  // V2 ticket support
  const isV2 = course?.ticketVersion === "v2";

  // Duration mode: "variable" puts ticket selection before date/time so slot
  // generation can use the chosen ticket's duration. Requires V2 tickets.
  // Shared with the orchestrator, which decides whether to fetch equipment
  // caps on the same question. Restating it in both places drifted three times.
  const isVariableDuration = isVariableDurationFlow({ course, isGroupSchedule });
  const durationMode: "fixed" | "variable" = isVariableDuration
    ? "variable"
    : "fixed";

  // Effective bundleID: pre-selected via prop (bundle redemption)
  const effectiveBundleID = bundleID || undefined;

  const base = useCheckoutBase({
    user,
    enabledAddOns: addOns,
    addOnGroups,
  });

  // Appointment-specific state
  const [step, setStep] = useState<CheckoutStep>(
    isVariableDuration ? "tickets" : "timeslot",
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(
    null,
  );
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [availableDates, setAvailableDates] = useState<any[]>([]);
  /**
   * Which `loadSlots` call owns the current state. Four callers and no
   * sequencing meant a slow request could land after a newer one and overwrite
   * it — and now that a failure CLEARS the grid, a losing request could wipe a
   * winning one's results and the selection `handleLocationFilterChange` had
   * just preserved.
   */
  const loadSlotsRequestId = useRef(0);
  /**
   * The same protection for the next-available search. It walks up to six
   * months sequentially, so it outlives almost anything that supersedes it —
   * an abandoned ticket or filter could stamp a date computed for itself over
   * the current one's, and clear the spinner while that search still runs.
   */
  const nextAvailableRequestId = useRef(0);
  /**
   * Bumped by every action that invalidates a pending next-available search —
   * a location change, a month change, a ticket change.
   *
   * ⚠ Request ids cannot order these: a superseded handler resumes AFTER the
   * winner, so its search would start second and take the HIGHER id,
   * overwriting the answer that was already right. And keying on the location
   * alone was not enough — `handleMonthChange` and the ticket effect both
   * reload without touching it, so a stale filter handler would still fire a
   * search carrying the pre-change month and the abandoned ticket. What
   * matters is whether anything has taken over since this handler began.
   *
   * ⚠ Read ONLY by the location handler, deliberately. `searchNextAvailable`
   * itself must not consult this: a context bump has no successor to clear
   * `isSearchingNextAvailable`, so honouring it there stranded the spinner.
   */
  const searchContextVersion = useRef(0);
  /** The ticket id `availableDates` was fetched with, or null if none was. */
  const [slotsFetchedForTicketTypeId, setSlotsFetchedForTicketTypeId] =
    useState<string | null>(null);
  const [locationsById, setLocationsById] = useState<
    Record<string, { name: string }> | undefined
  >(undefined);
  const [locationFilter, setLocationFilter] = useState<string | null>(null);
  const [locationOptions, setLocationOptions] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [scheduleHasMultipleLocations, setScheduleHasMultipleLocations] =
    useState(false);
  // "Location first" mode from the API. Written by the same unfiltered
  // initial load as locationOptions, so it is known before the step renders.
  const [locationSelectionMode, setLocationSelectionMode] = useState<
    "optional" | "required"
  >("optional");
  const [nextAvailableDateInfo, setNextAvailableDateInfo] = useState<{
    dateISO: string;
    month: number;
    year: number;
  } | null>(null);
  const [isSearchingNextAvailable, setIsSearchingNextAvailable] =
    useState(false);

  // Guest validation state
  const [guestTouched, setGuestTouched] = useState<
    Array<Partial<Record<string, boolean>>>
  >([]);

  // Compute guest validation errors
  const guestErrors = useMemo(() => {
    return base.personalDetails.guests.map((guest: any, index: number) =>
      validateGuest(
        {
          firstName: guest.firstName || "",
          lastName: guest.lastName || "",
          email: guest.email || "",
          phoneNumber: guest.phoneNumber || "",
        },
        {
          isFirstGuest: index === 0,
          requirePhone: true,
        },
      ),
    );
  }, [base.personalDetails.guests]);

  // Handle guest field blur for validation
  const handleGuestBlur = useCallback((guestIndex: number, field: string) => {
    setGuestTouched((prev) => {
      const newTouched = [...prev];
      if (!newTouched[guestIndex]) {
        newTouched[guestIndex] = {};
      }
      newTouched[guestIndex] = {
        ...newTouched[guestIndex],
        [field]: true,
      };
      return newTouched;
    });
  }, []);

  // Group schedule: guest count state (V1 only)
  const [groupGuestCount, setGroupGuestCount] = useState<number | null>(null);

  // V2: ticket selections state (managed by TicketSelectionStepV2)
  const [ticketSelections, setTicketSelections] = useState<TicketSelection[]>([]);

  const updateTicketSelection = useCallback(
    (ticketTypeId: string, quantity: number) => {
      setTicketSelections((prev) => {
        if (quantity === 0) return prev.filter((s) => s.ticketTypeId !== ticketTypeId);
        const existing = prev.find((s) => s.ticketTypeId === ticketTypeId);
        if (existing) return prev.map((s) => s.ticketTypeId === ticketTypeId ? { ...s, quantity } : s);
        return [...prev, { ticketTypeId, quantity }];
      });
    },
    [],
  );

  /**
   * The schedule slots the SERVER keys on: a group listing keeps its durations
   * in `groupScheduleSlots`, keyed on `isGroupSchedule` — NOT on `isV2`, which
   * is what the `pricingOptions` arm below gets wrong. Shared with the
   * duration resolver so both read the same configuration.
   */
  const durationScheduleSlots = useMemo(
    () =>
      (isGroupSchedule
        ? (course?.groupScheduleSlots || [])
        : (course?.appointmentScheduleSlots || [])
      // ⚠ Disabled slots are invisible to the server: `slotsForDay` requires
      // `status === "enabled"` (GetCourseAvailableSlots.controller.js:651), so
      // nothing they configure is bookable. Leaving them in let a school's OLD
      // 30-minute slot keep answering for a ticket after they replaced it with
      // a 60-minute one, because this map is first-wins over the raw array.
      ).filter((slot: any) => slot?.status === "enabled"),
    [isGroupSchedule, course?.appointmentScheduleSlots, course?.groupScheduleSlots],
  );

  /**
   * ticketTypeId → configured duration, and the ticket types that index cannot
   * speak for. Built from the schedule slots by `buildTicketDurationIndex`,
   * which is where the rules and their tests live.
   *
   * ⚠ Read this map, never `pricingOptions[].duration`: that is already
   * `?? tt.duration ?? 60`, so an unknown length arrives as a confident hour
   * and the resolver's omit-when-unknowable contract goes dead.
   */
  /**
   * Ticket-type identity, for resolving the LEGACY link: a V1 listing leaves
   * `slot.tickets[].ticketTypes` empty and puts the slot ticket's `_id` in the
   * ticket type's `ticketsIds` instead. Without it those listings index
   * nothing and never show a window at all.
   *
   * ⚠ Identity only — no duration rides on these. Neither schema has one, so
   * an earlier attempt to read `tt.duration` here was dead code.
   */
  const ticketTypeLinks = useMemo(
    () => (isV2 ? ticketTypesV2 : ticketTypes) || [],
    [isV2, ticketTypesV2, ticketTypes],
  );

  const { durationByTicketType, ticketTypesWithVaryingDuration } = useMemo(
    () =>
      buildTicketDurationIndex(
        durationScheduleSlots,
        ticketTypeLinks,
        // Leaving per-ticket durations unset is a valid configuration; the
        // server falls back to the listing's own length, so this must too or
        // every ticket on such a listing looks unknowable.
        course?.duration,
      ),
    [durationScheduleSlots, ticketTypeLinks, course?.duration],
  );

  // Map ticket types to pricing options
  // Duration is stored on schedule slots' tickets[].duration, not on ticket types
  const pricingOptions: any[] = useMemo(() => {
    // ⚠ Reads `durationByTicketType`, which keys on `isGroupSchedule` exactly
    // as the server does. This arm used to key on `isV2` and read
    // `appointmentScheduleSlots` for every V2 listing, so a V2 GROUP listing —
    // whose durations live in `groupScheduleSlots` — found an empty map and
    // fabricated `?? tt.duration ?? 60` on every option card, while the basket
    // window (built from the correct array) showed the real length. One source
    // for both, so the two surfaces cannot disagree.
    const durationMap = durationByTicketType;

    /**
     * The card's length, or nothing when the listing gives this ticket no
     * single dependable one.
     *
     * ⚠ `durationMap` is first-wins across the listing's enabled slots, which
     * is all a per-listing index can be — the server resolves per slot per
     * date. Until this branch that arbitrary answer was the only length on
     * screen, so it was wrong quietly. Now `selectedDurationMinutes` resolves
     * the real one from the SELECTED date's slots and prints it in the header,
     * the footer and the basket row, so a first-wins card contradicts three
     * surfaces at once: a ticket configured 30m Mon–Fri and 60m on Saturday
     * reads "30min" on the card beside "09:00–10:00 (1h)" everywhere else.
     * `ticketTypesWithVaryingDuration` is the signal built for exactly this —
     * it holds every ticket the map cannot speak for, whether because slots
     * disagree or because one sells it with no length at all.
     *
     * ⚠ The `?? 60` stays for tickets the index simply never saw. Those are
     * absent from BOTH the map and the set — a ticket configured only on a
     * disabled slot, say — and silencing them here would be card `1o`'s fix,
     * not this one. Same fabricated hour, different cause; left untouched so
     * the two stay separable.
     */
    const optionDuration = (id: unknown, ticketTypeDuration: unknown) =>
      ticketTypesWithVaryingDuration.has(String(id))
        ? undefined
        : (durationMap.get(String(id)) ?? ticketTypeDuration ?? 60);

    if (isV2) {
      return ticketTypesV2
        .map((tt: any) => ({
          id: tt.id,
          name: tt.name,
          description: tt.description,
          price: tt.price,
          duration: optionDuration(tt.id, tt.duration),
          chargeType: tt.chargeType,
          chargeConfig: tt.chargeConfig,
          minTicketsPerOrder: tt.minTicketsPerOrder ?? 1,
          maxTicketsPerOrder: tt.maxTicketsPerOrder ?? 100,
          numberOfGuestsPerTicket: 1,
          isV2: true,
        }))
        .sort((a: any, b: any) => b.price - a.price);
    }

    return ticketTypes
      .map((tt: any) => ({
        id: tt._id,
        name: tt.name,
        description: tt.description,
        price: tt.price,
        duration: optionDuration(tt._id, tt.duration),
        ticketsIds: tt.ticketsIds || [tt._id],
        numberOfGuestsPerTicket: tt.numberOfGuestsPerTicket ?? 1,
        minTicketsPerOrder: tt.minTicketsPerOrder ?? 1,
        maxTicketsPerOrder: tt.maxTicketsPerOrder ?? 100,
      }))
      .sort((a, b) => b.price - a.price);
  }, [
    ticketTypes,
    ticketTypesV2,
    durationByTicketType,
    ticketTypesWithVaryingDuration,
    isV2,
  ]);

  const selectedOption =
    selectedOptionIndex !== null ? pricingOptions[selectedOptionIndex] : null;


  // V1: guest count logic
  const allowMultipleTickets =
    course?.appointmentScheduleSettings?.allowMultipleTickets ?? true;
  const showGuestCount = !isV2 && allowMultipleTickets;

  useEffect(() => {
    if (showGuestCount && selectedOption) {
      setGroupGuestCount(selectedOption.minTicketsPerOrder);
    }
  }, [showGuestCount, selectedOption?.id]);

  const totalGuests = showGuestCount
    ? (groupGuestCount ?? selectedOption?.minTicketsPerOrder ?? 1)
    : isV2
      ? ticketSelections.reduce((sum, s) => sum + s.quantity, 0) || 1
      : (selectedOption?.numberOfGuestsPerTicket ?? 1);

  // The ticket count sent to the API
  const ticketCount = isV2
    ? ticketSelections.reduce((sum, s) => sum + s.quantity, 0) || 1
    : showGuestCount ? totalGuests : 1;

  // V2 pricing: build synthetic session for the pricing engine
  const syntheticSession = useMemo(() => {
    if (!selectedDate || selectedTime === null) return null;
    const hours = Math.floor(selectedTime / 60);
    const minutes = selectedTime % 60;
    const courseTimezone = timezone || course.timezone || "Europe/London";
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")} ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
    const startDateTime = moment.tz(dateStr, courseTimezone).toISOString();
    return { startDateTime };
  }, [selectedDate, selectedTime, timezone, course.timezone]);

  // The slot the customer has picked, resolved in BOTH duration modes.
  //
  // Split out of selectedSlotPublicInfo deliberately. The equipment cap is
  // meaningful in variable mode — the server always receives a ticketTypeId
  // there, so every slot it returns carries poolMaxTickets — but the
  // PUBLIC-SESSION half of the old memo must stay fixed-mode-only (see below).
  // One lookup, two derivations.
  /** EVERY slot at the selected start — one per ticket, see the duration memo. */
  const selectedSlotsAtTime = useMemo<any[]>(() => {
    if (!selectedDate || selectedTime === null) return [];
    const courseTimezone = timezone || course.timezone || "Europe/London";
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();
    const selectedDateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const dateData = availableDates.find((d: any) => {
      const dStr = moment.tz(d.dateISO, courseTimezone).format("YYYY-MM-DD");
      return dStr === selectedDateStr;
    });
    return (dateData?.availableSlots ?? []).filter(
      (s: any) => s.startMinutes === selectedTime,
    );
  }, [selectedDate, selectedTime, availableDates, timezone, course.timezone]);

  /** The first slot at the selected start. See selectedSlotsAtTime — there
   *  can be several, one per ticket, and they differ in length. */
  const selectedSlotRaw = useMemo(
    () => selectedSlotsAtTime[0] ?? null,
    [selectedSlotsAtTime],
  );

  /**
   * The ticket types this booking is actually for.
   *
   * ⚠ Stringified, like every other id comparison in this flow: the index they
   * are matched against keys with `String(...)`, and the course payload has
   * been seen with ObjectId and string ids both ways. An unstringified id
   * misses the map, so the option card falls to `?? tt.duration ?? 60` and
   * shows a fabricated hour beside a basket window carrying the real length.
   */
  const chosenTicketTypeIds = useMemo<string[]>(
    () =>
      isV2
        ? ticketSelections
            .filter((sel) => sel.quantity > 0)
            .map((sel) => String(sel.ticketTypeId))
        : selectedOption?.id
          ? [String(selectedOption.id)]
          : [],
    [isV2, ticketSelections, selectedOption],
  );

  /**
   * How long the chosen appointment actually runs, or null when it cannot yet
   * be known. The resolution order — and the wrong answers shipped before it
   * settled — live with the function, in `../utils/appointment-duration`,
   * where they are covered by tests.
   *
   * ⚠ Passes the RAW `durationByTicketType` map, never `pricingOptions[]`:
   * every option's duration is already `?? tt.duration ?? 60`, so an unknown
   * length would arrive as a confident hour and the resolver's whole
   * omit-when-unknowable contract would go dead. That applies to
   * `selectedOption.duration` too — only its `id` is used here.
   *
   * ⚠ `isVariableDuration` is load-bearing, and it mirrors what the SERVER
   * FILTERS on, not what the client sends. `loadSlots` passes a
   * `selectedTicketTypeId` in fixed mode too (see appointmentCapTicketTypeId —
   * the equipment cap needs it), but the server only narrows the ticket list
   * when `!isGroupSchedule && durationMode === "variable" && ticketTypeId`
   * (GetCourseAvailableSlots.controller.js:639). So in fixed mode `ticketIndex`
   * still indexes the UNFILTERED configured array and can be traced back; in
   * variable mode it cannot, and the slots are trusted wholesale instead.
   *
   * ⚠ `isVariableDurationFlow` carries a third clause the server's condition
   * does not — `ticketVersion === "v2"` — and two review rounds have called
   * that a mismatch. It is not reachable: a non-v2 course gets
   * `ticketTypesV2: []` from the widget controller
   * (GetCourseWithBundles.controller.ts:318-321), so `ticketTypeIds` is empty,
   * `appointmentCapTicketTypeId` returns undefined, no id is sent, and the
   * server's `&& ticketTypeId` clause is falsy. A non-v2 listing can never
   * receive a filtered response.
   */
  const selectedDurationMinutes = useMemo<number | null>(
    () =>
      resolveAppointmentDurationMinutes({
        // Empty until a date AND a time are picked.
        slotsAtSelectedStart: selectedSlotsAtTime,
        scheduleSlots: durationScheduleSlots,
        // Mirrors the server's `!isGroupSchedule && durationMode === "variable"
        // && ticketTypeId`, and then goes one further: the id has to be the
        // one now chosen. Reading the mode alone called an unfiltered mount
        // response filtered; knowing only that SOME id was sent still lets the
        // resolver trust a previous ticket's slots wholesale — a refetch that
        // fails leaves the old grid on screen with the flag still true, and
        // the stamped duration is then the abandoned service's.
        slotsAreTicketFiltered:
          isVariableDuration &&
          chosenTicketTypeIds.length === 1 &&
          slotsFetchedForTicketTypeId === chosenTicketTypeIds[0],
        chosenTicketTypeIds,
        durationByTicketType,
        ticketTypeLinks,
        ticketTypesWithVaryingDuration,
      }),
    [
      selectedSlotsAtTime,
      durationScheduleSlots,
      isVariableDuration,
      slotsFetchedForTicketTypeId,
      chosenTicketTypeIds,
      durationByTicketType,
      ticketTypeLinks,
      ticketTypesWithVaryingDuration,
    ],
  );

  // Detect whether the selected appointment slot corresponds to a public
  // session (the API annotates those slots with `publicSessionId` /
  // `hasPublicSession`). Fixed-mode only: in variable mode the ticket is
  // chosen before the slot, so this filter has no meaning.
  //
  // ⚠ The isVariableDuration guard is LOAD-BEARING and must not be removed to
  // "simplify" this. selectedSlotIsPublic gates the public-ticket filter and
  // the selection-purge effect below; in variable mode the ticket has already
  // been chosen, so a non-null value here would DELETE the customer's ticket
  // selection the instant they pick a time.
  const selectedSlotPublicInfo = useMemo(() => {
    if (isVariableDuration) return null;
    if (!selectedSlotRaw) return null;
    const isPublic = !!(
      selectedSlotRaw.hasPublicSession || selectedSlotRaw.publicSessionId
    );
    return {
      isPublic,
      spotsRemaining:
        typeof selectedSlotRaw.spotsRemaining === "number"
          ? selectedSlotRaw.spotsRemaining
          : null,
    };
  }, [isVariableDuration, selectedSlotRaw]);

  // Equipment left on the picked slot's pool — BOTH modes, which is the whole
  // point of the split above. null = no pool, or the server was never asked
  // (fail open, never sold out).
  const selectedSlotPoolMaxTickets = useMemo(
    () =>
      typeof selectedSlotRaw?.poolMaxTickets === "number"
        ? selectedSlotRaw.poolMaxTickets
        : null,
    [selectedSlotRaw],
  );

  const selectedSlotIsPublic =
    selectedSlotPublicInfo === null ? null : selectedSlotPublicInfo.isPublic;

  // Selected slot's effective location name, surfaced on the payment step's
  // summary and the sidebar whenever the slot resolves to a known location
  // (single-location listings included — design 2026-08-12).
  const selectedSlotLocationLabel = useMemo(() => {
    if (!selectedDate || selectedTime === null) return undefined;
    const courseTimezone = timezone || course.timezone || "Europe/London";
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();
    const selectedDateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const dateData = availableDates.find((d) => {
      const dStr = moment.tz(d.dateISO, courseTimezone).format("YYYY-MM-DD");
      return dStr === selectedDateStr;
    });
    const matchingSlot = dateData?.availableSlots?.find(
      (s: { startMinutes: number; locationId?: string }) =>
        s.startMinutes === selectedTime,
    );
    const locationId = matchingSlot?.locationId;
    if (!locationId || locationId === "-1") return undefined;
    return locationsById?.[locationId]?.name;
  }, [
    selectedDate,
    selectedTime,
    availableDates,
    timezone,
    course.timezone,
    locationsById,
  ]);

  // Sidebar location tag: selected slot's location → active filter's location
  // → the listing's only location → generic "In-person" (handled by the card
  // when this is undefined). Online listings ignore it entirely.
  const sidebarLocationLabel = useMemo(() => {
    if (course.isOnline) return undefined;
    if (selectedSlotLocationLabel) return selectedSlotLocationLabel;
    if (locationFilter) {
      return locationOptions.find((o) => o.id === locationFilter)?.name;
    }
    if (locationOptions.length === 1) return locationOptions[0]!.name;
    return undefined;
  }, [
    course.isOnline,
    selectedSlotLocationLabel,
    locationFilter,
    locationOptions,
  ]);

  // Filter v2 tickets by the selected slot's session mode.
  //   - No slot picked yet (or variable mode): show the full list.
  //   - Slot already has an active public session: only public-mode tickets
  //     can join that shared session, so hide private-mode tickets.
  //   - Slot has no existing public session: show every ticket. Public-mode
  //     tickets will seed a new public session on booking; private-mode
  //     tickets behave as a regular private appointment. Otherwise public
  //     tickets could never be booked the first time (chicken-and-egg).
  const effectiveTicketTypesV2 = useMemo(() => {
    if (selectedSlotIsPublic === null) return ticketTypesV2;
    if (!selectedSlotIsPublic) return ticketTypesV2;
    return ticketTypesV2.filter(
      (tt: any) => (tt.sessionMode || "private") === "public",
    );
  }, [ticketTypesV2, selectedSlotIsPublic]);

  // If the user already had selections that no longer belong to the
  // filtered ticket list (e.g. switched from a private slot to a public
  // one), drop them so stale picks don't linger into payment.
  useEffect(() => {
    if (selectedSlotIsPublic === null) return;
    setTicketSelections((prev) => {
      const allowedIds = new Set(
        effectiveTicketTypesV2.map((tt: any) => tt.id),
      );
      const filtered = prev.filter((sel) => allowedIds.has(sel.ticketTypeId));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [effectiveTicketTypesV2, selectedSlotIsPublic]);

  const v2 = useV2Pricing({
    enabled: isV2,
    ticketTypesV2: effectiveTicketTypesV2,
    ticketSelections,
    selectedSession: syntheticSession,
    rawSessionTicketTypes: [],
    numberOfSessions: 1,
    courseTimezone: timezone || course.timezone || "Europe/London",
    discount: base.discount,
    addOnsTotal: base.addOnsTotal,
    bundleID,
    cartPricingRules,
    priorPurchasedTicketIds,
    customerBookingHistory,
  });

  // Calculate total
  // For bundle bookings, total is 0 (payment was done when purchasing bundle)
  const v1Total = useMemo(() => {
    if (bundleID) return 0;
    const optionPrice = selectedOption?.price || 0;
    const grossTotal = optionPrice * ticketCount + base.addOnsTotal;
    let disc = 0;
    if (base.discount) {
      const { value, type } = base.discount;
      if (type === "PERCENTAGE") {
        disc = Math.round((grossTotal * Math.min(value, 100)) / 100);
      } else {
        disc = Math.min(grossTotal, value);
      }
    }
    return Math.max(0, grossTotal - disc);
  }, [selectedOption, base.addOnsTotal, base.discount, bundleID, ticketCount]);

  const total = bundleID ? 0 : isV2 ? v2.total : v1Total;

  const v1DiscountAmount = useMemo(() => {
    if (!base.discount) return 0;
    const optionPrice = selectedOption?.price || 0;
    const grossTotal = optionPrice * ticketCount + base.addOnsTotal;
    const { value, type } = base.discount;
    if (type === "PERCENTAGE") {
      return Math.round((grossTotal * Math.min(value, 100)) / 100);
    }
    return Math.min(grossTotal, value);
  }, [base.discount, selectedOption, base.addOnsTotal, ticketCount]);

  const discountAmount = isV2 ? v2.discountAmount : v1DiscountAmount;

  const discountPercentage = useMemo(() => {
    if (!base.discount || base.discount.type !== "PERCENTAGE") return undefined;
    return base.discount.value;
  }, [base.discount]);

  // Visible steps
  const visibleSteps = useMemo(() => {
    const steps: CheckoutStep[] = [];

    if (isVariableDuration) {
      // Variable duration: ticket first so slot API knows the duration to use
      steps.push("tickets", "timeslot");
    } else {
      steps.push("timeslot");
      // V2: add a dedicated tickets step for quantity selection using the shared component
      if (isV2 && allowMultipleTickets) {
        steps.push("tickets");
      }
    }

    // Skip add-ons for bundle bookings (pre-selected via prop, e.g. from bundle manage page)
    if ((addOns.length > 0 || addOnGroups.length > 0) && !bundleID) {
      steps.push("addons");
    }

    // Participants ("Who is attending?") — collected before the booking-question
    // form when the school uses attendees.
    if (showParticipantsStep) {
      steps.push("participants");
    }

    // System identity questions never render here (this flow keeps the personal
    // step); hide the userform step if it would have nothing to show.
    if (
      course.userQuestionForm?.isEnabled &&
      hasRenderableQuestions(course.userQuestionForm?.questions, totalGuests, false, showParticipantsStep)
    ) {
      steps.push("userform");
    }

    // Skip the personal step when the basket checkout will collect buyer info
    // downstream. Single-guest bookings always skip (the buyer is the only
    // guest); multi-guest bookings skip only when the listing has opted in
    // to prefilling other guests with buyer info — otherwise this flow still
    // collects each guest here.
    const skipPersonalStep =
      totalGuests === 1 || allowPrefillOthersGuestsOnCheckout;

    if (!skipPersonalStep) {
      steps.push("personal");
    }

    steps.push("basketChoice");

    return steps;
  }, [addOns, addOnGroups, course.userQuestionForm, bundleID, isVariableDuration, isV2, allowMultipleTickets, allowPrefillOthersGuestsOnCheckout, totalGuests, showParticipantsStep]);

  const currentStepIndex = visibleSteps.indexOf(step);
  const isFirstStep = currentStepIndex === 0;

  const goToNextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < visibleSteps.length) {
      setStep(visibleSteps[nextIndex]!);
    }
  }, [currentStepIndex, visibleSteps]);

  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setStep(visibleSteps[prevIndex]!);
    }
  }, [currentStepIndex, visibleSteps]);

  const goToStep = useCallback(
    (targetStep: CheckoutStep) => {
      if (visibleSteps.includes(targetStep)) {
        setStep(targetStep);
      }
    },
    [visibleSteps],
  );

  // Call step change callback for scroll-to-top behavior
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  // Load available slots.
  //
  // Sent for FIXED-duration appointments too, which is the whole of U1's
  // client half: the server's pool block only runs when the request names a
  // ticket type, so gating this on variable-duration meant the cap was never
  // computed for the common appointment listing and the customer could pick
  // more equipment than exists.
  const selectedTicketTypeId = appointmentCapTicketTypeId({
    isVariableDuration,
    selectedTicketTypeId: ticketSelections[0]?.ticketTypeId,
    ticketTypeIds: v2.ticketTypes.map(t => t.id),
  });

  // The selector already renders a per-ticket cap; the appointment flow simply
  // never passed one. Fixed-duration only — in variable mode the ticket is
  // chosen before the slot, so there is no slot to read a cap from yet.
  // FIXED-DURATION ONLY, deliberately.
  //
  // This map carries exactly one entry — the ticket the server measured — while
  // its pool SIBLINGS stay uncapped. In fixed mode that is harmless: the cap is
  // only computed when the listing has a single ticket type, so there are no
  // siblings. In variable mode siblings exist, and an uncapped sibling can be
  // raised until poolCapAcrossTicketTypes drives the measured ticket's cap to
  // 0, marking its row sold out.
  //
  // ⚠ The hazard that ORIGINALLY justified this gate is GONE. The selector no
  // longer hides the decrement on a sold-out row that holds a quantity, so
  // that state is recoverable rather than the dead end it was — a customer
  // could neither remove nor book it, and it still shipped in the order. What
  // remains is milder: a row reading "Sold out" while a sibling holds the
  // stock.
  //
  // So re-enabling this in variable mode is now a product call, not a
  // correctness one. Left off because it was gated off before QA signed the
  // flow off, not because it is unsafe. Variable mode's cap surface is the
  // slot grid (requestedPoolQuantity below), which needs no per-ticket map.
  //
  // Two sources, one shape. The single-ticket path produces a scalar the
  // server stamped as slot.poolMaxTickets; the multi-ticket path produces the
  // per-ticket map. Exactly one is ever populated — the client sends
  // poolTicketTypeIds only when it could not name a single ticketTypeId — so
  // this fallback never has to arbitrate a conflict.
  const poolMaxTicketsByTicketTypeId = useMemo(
    () =>
      isVariableDuration
        ? undefined
        : appointmentPoolCaps(selectedTicketTypeId, selectedSlotPoolMaxTickets) ??
          appointmentPoolCapsFromSlot(
            selectedSlotRaw?.poolMaxTicketsByTicketTypeId,
          ),
    [
      isVariableDuration,
      selectedTicketTypeId,
      selectedSlotPoolMaxTickets,
      selectedSlotRaw,
    ],
  );

  // The demand the slot grid must be measured against, in tickets of the one
  // ticket type the server computed the cap for.
  //
  // NOT that ticket's own quantity: poolMaxTickets bounds the shared POOL, so
  // siblings drawing on the same pool spend the same stock. Counting only the
  // measured ticket let 2 Adult + 2 Child through a 3-unit pool. Tickets on a
  // DIFFERENT pool are still ignored — they have nothing to do with this cap.
  const requestedPoolQuantity = useMemo(
    () =>
      poolDemandInTicketsOf({
        measuredTicketTypeId: selectedTicketTypeId,
        ticketTypes: v2.ticketTypes,
        selections: ticketSelections,
      }),
    [ticketSelections, selectedTicketTypeId, v2.ticketTypes],
  );

  // The same demand left in POOL UNITS. Paired with each slot's unrounded
  // poolFreeUnits this is exact, which matters when the measured ticket costs
  // more than one unit: maxTickets floors, so a 2-unit + 1-unit pair against
  // 3 free units would otherwise be refused outright rather than merely
  // rounded. null when the measured ticket has no pool, or an older API sent
  // no freeUnits — both fall back to the ticket comparison.
  const requestedPoolUnits = useMemo(
    () =>
      poolDemandUnitsOnPool({
        measuredTicketTypeId: selectedTicketTypeId,
        ticketTypes: v2.ticketTypes,
        selections: ticketSelections,
      }),
    [ticketSelections, selectedTicketTypeId, v2.ticketTypes],
  );

  // The picked slot's unrounded free units, mode-independent like its cap.
  const selectedSlotPoolFreeUnits = useMemo(
    () =>
      typeof selectedSlotRaw?.poolFreeUnits === "number"
        ? selectedSlotRaw.poolFreeUnits
        : null,
    [selectedSlotRaw],
  );

  const loadSlots = useCallback(
    async (month: number, year: number, locationId?: string | null) => {
      base.setIsLoading(true);
      // ⚠ Captured, not re-read. The SERVER narrows its slot list only when a
      // ticket id reaches it, so whether the response we are about to store is
      // ticket-filtered is a property of THIS call — not of the mode, and not
      // of whatever is selected by the time it lands.
      const fetchedForTicketTypeId = selectedTicketTypeId ?? null;
      const requestId = ++loadSlotsRequestId.current;
      const isStale = () => requestId !== loadSlotsRequestId.current;
      try {
        const result = await onLoadAvailableSlots(
          month,
          year,
          selectedTicketTypeId,
          (locationId === undefined ? locationFilter : locationId) ?? undefined,
        );
        const dates = result.dates || [];
        // ⚠ null, not the data. Skipping the WRITE is not enough: the caller
        // acts on what it gets back, so handing a superseded request's dates
        // to `handleLocationFilterChange` let it clear a selection the winning
        // request had just preserved — the very bug this ref was added for.
        if (isStale()) return null;
        setAvailableDates(dates);
        setSlotsFetchedForTicketTypeId(fetchedForTicketTypeId);
        setLocationsById(result.locationsById);
        if (result.locationOptions) setLocationOptions(result.locationOptions);
        if (typeof result.hasMultipleLocations === "boolean") {
          setScheduleHasMultipleLocations(result.hasMultipleLocations);
        }
        if (result.locationSelectionMode) {
          setLocationSelectionMode(result.locationSelectionMode);
        }
        return dates;
      } catch (error) {
        // ⚠ Drop the grid along with the error. Leaving the previous response
        // on screen is not a harmless stale read: in variable-duration mode it
        // belongs to the ticket the customer just switched AWAY from, and the
        // ⚠ Nothing is torn down in FIXED-duration mode. The grid is only
        // misreadable in variable mode — there the server renumbers
        // `ticketIndex` inside the filtered array, so the previous ticket's
        // slots resolve to the wrong configured one and a time picked from
        // them is sized by the abandoned service. In fixed mode the index
        // still refers to the unfiltered array, so the old grid is merely
        // stale, not wrong.
        //
        // ⚠ And emptying it there is actively DANGEROUS. The selection is
        // kept, so `selectedSlotRaw` goes null while a time is still chosen —
        // which takes `selectedSlotPoolMaxTickets` to null, and `slotPoolFit`
        // fails OPEN on a null cap by design ("never to sold out"). The
        // equipment cap disappears from the ticket step, `canContinue` passes,
        // and the customer can raise quantities past the pool. A failed
        // refetch must not be able to unlock an oversell.
        if (isStale()) return null;
        if (isVariableDuration) {
          setAvailableDates([]);
          setSlotsFetchedForTicketTypeId(null);
          setSelectedDate(null);
          setSelectedTime(null);
          setSelectedOptionIndex(null);
        }
        base.setError("Failed to load available times");
        // null, not `[]` — the caller reads an empty array as "your date is no
        // longer available" and would clear the selection this just kept.
        return null;
      } finally {
        // Stale-guarded like the writes above: a superseded request clearing
        // the spinner leaves the PREVIOUS filter's grid on screen looking
        // settled while the newest fetch is still running.
        if (!isStale()) base.setIsLoading(false);
      }
    },
    [onLoadAvailableSlots, selectedTicketTypeId, locationFilter, isVariableDuration],
  );

  // Search for next available date across future months
  const searchNextAvailable = useCallback(
    async (startMonth: number, startYear: number, locationId?: string | null) => {
      const requestId = ++nextAvailableRequestId.current;
      // ⚠ Request id ONLY — deliberately, after a reverted attempt to also
      // honour `searchContextVersion`. Every exit below skips clearing
      // `isSearchingNextAvailable` when stale, which is safe against a newer
      // SEARCH (its successor owns the flag) and unsafe against a context bump
      // (`handleMonthChange` invalidates without starting a replacement). That
      // version left the spinner running for the rest of the session and took
      // the "next available" escape hatch with it — a permanent, unrecoverable
      // failure traded for a cosmetic one. If a context-aware version is ever
      // wanted, it has to clear the flag on the context-stale path first.
      const isStale = () => requestId !== nextAvailableRequestId.current;
      setIsSearchingNextAvailable(true);
      setNextAvailableDateInfo(null);

      const effectiveLocationId =
        (locationId === undefined ? locationFilter : locationId) ?? undefined;

      // Search up to 6 months ahead
      const maxMonthsToSearch = 6;
      let month = startMonth;
      let year = startYear;

      for (let i = 0; i < maxMonthsToSearch; i++) {
        try {
          const result = await onLoadAvailableSlots(month, year, selectedTicketTypeId, effectiveLocationId);
          const dates = result.dates || [];

          // Find first date that has available slots and is not disabled
          const availableDate = dates.find(
            (d: any) => !d.isDisabled && d.availableSlots?.length > 0,
          );

          if (availableDate) {
            if (isStale()) return;
            setNextAvailableDateInfo({
              dateISO: availableDate.dateISO,
              month,
              year,
            });
            setIsSearchingNextAvailable(false);
            return;
          }
        } catch (error) {
          // Continue searching even if one month fails
        }

        // Move to next month
        month++;
        if (month > 12) {
          month = 1;
          year++;
        }
      }

      if (!isStale()) setIsSearchingNextAvailable(false);
    },
    [onLoadAvailableSlots, selectedTicketTypeId, locationFilter],
  );

  // Handle month change from calendar
  const handleMonthChange = useCallback(
    (date: Date) => {
      setCurrentMonth(date);
      // Reset selections when month changes
      setSelectedDate(null);
      setSelectedTime(null);
      setSelectedOptionIndex(null);
      // Stops a PENDING location-filter handler from starting a search for the
      // month being left. It does not cancel a search already running — see
      // the note in `searchNextAvailable`.
      searchContextVersion.current++;
      // Load slots for the new month
      loadSlots(date.getMonth() + 1, date.getFullYear());
    },
    [loadSlots],
  );

  // Location filter change: refetch the current month under the new filter,
  // keep the selected date if it still has matching slots, and re-run the
  // next-available search so its label respects the filter.
  const handleLocationFilterChange = useCallback(
    async (locationId: string | null) => {
      setLocationFilter(locationId);
      const searchContext = ++searchContextVersion.current;
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const dates = await loadSlots(month, year, locationId);
      // ⚠ No usable dates — superseded by a newer load, or the request failed.
      // Either way the selection work has nothing to judge against, and an
      // empty array would read as "your date is gone" and clear it.
      //
      // The next-available refresh is decided separately, at the end: what
      // supersedes this load is not always another filter change, and
      // `handleMonthChange` bumps the same counter without scheduling a
      // search, so bailing outright left the label holding the pre-filter
      // answer with nothing to recompute it.
      const noUsableDates = dates === null;
      if (!noUsableDates && selectedDate) {
        const courseTimezone = timezone || course.timezone || "Europe/London";
        const selectedDateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
        const dateData = dates.find(
          (d: any) =>
            moment.tz(d.dateISO, courseTimezone).format("YYYY-MM-DD") ===
            selectedDateStr,
        );
        const dateStillAvailable =
          !!dateData &&
          !dateData.isDisabled &&
          (dateData.availableSlots?.length ?? 0) > 0;
        if (!dateStillAvailable) {
          setSelectedDate(null);
          setSelectedTime(null);
          setSelectedOptionIndex(null);
        } else if (selectedTime !== null) {
          // Resolved with `find`, NOT `some`, so this agrees with
          // selectedSlotRaw about which slot the selection means. With `some`
          // the two could disagree when several slots share a start time: the
          // filter would keep a selection because SOME variant fits while the
          // continue gate, reading the first, refused it.
          const stillThere = dateData.availableSlots.find(
            (s: any) => s.startMinutes === selectedTime,
          );
          // Dropped when the time is gone, and also when the reload came back
          // with a tighter cap for it — the refetch can change the window's
          // occupancy under a live selection, which would otherwise leave a
          // slot rendered selected-and-disabled with Continue still live.
          const stillFits =
            !!stillThere &&
            slotPoolFit({
              poolMaxTickets: stillThere.poolMaxTickets,
              poolFreeUnits: stillThere.poolFreeUnits,
              requestedQuantity: requestedPoolQuantity,
              requestedUnits: requestedPoolUnits,
            }).fits;
          if (!stillFits) {
            setSelectedTime(null);
            setSelectedOptionIndex(null);
          }
        }
      }
      // ⚠ Only while nothing has taken over — see `searchContextVersion`. The
      // month and the ticket are baked into this closure as much as the
      // location is, so any of the three moving on makes this search's answer
      // the wrong one to stamp.
      if (searchContextVersion.current !== searchContext) return;
      searchNextAvailable(month, year, locationId);
    },
    [
      currentMonth,
      loadSlots,
      searchNextAvailable,
      selectedDate,
      selectedTime,
      requestedPoolQuantity,
      requestedPoolUnits,
      timezone,
      course.timezone,
    ],
  );

  // Initial load
  useEffect(() => {
    // Runs in every mode. It is also the only writer of `locationOptions` and
    // `scheduleHasMultipleLocations`, so skipping it left a variable-duration
    // listing's tickets step showing "In-person" instead of the venue. The
    // unfiltered response it produces is safe because
    // `slotsFetchedForTicketTypeId` records that it was unfiltered.
    const now = new Date();
    loadSlots(now.getMonth() + 1, now.getFullYear());
    // Also search for next available date
    searchNextAvailable(now.getMonth() + 1, now.getFullYear());
  }, []);

  // In variable-duration mode, reload slots when the selected ticket changes
  useEffect(() => {
    if (!isVariableDuration || !selectedTicketTypeId) return;
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedOptionIndex(null);
    // Stops a PENDING location-filter handler from searching for the ticket
    // being left; this effect starts its own search immediately below, which
    // supersedes any running one by request id.
    searchContextVersion.current++;
    loadSlots(currentMonth.getMonth() + 1, currentMonth.getFullYear());
    searchNextAvailable(
      currentMonth.getMonth() + 1,
      currentMonth.getFullYear(),
    );
  }, [selectedTicketTypeId, isVariableDuration]);

  // Validation
  const canContinue = useCallback((): boolean => {
    switch (step) {
      case "timeslot": {
        const hasTimeSelection = !!selectedDate && selectedTime !== null && selectedOptionIndex !== null;
        if (!hasTimeSelection) return false;
        // The grid renders a slot disabled when its equipment cannot cover the
        // chosen quantity, but a selection made BEFORE the cap changed is still
        // held in state. Without this the customer continues to payment on a
        // slot the grid itself is refusing. Fails open: no pool, no cap, or
        // nothing chosen yet all report fits.
        if (
          !slotPoolFit({
            poolMaxTickets: selectedSlotPoolMaxTickets,
            poolFreeUnits: selectedSlotPoolFreeUnits,
            requestedQuantity: requestedPoolQuantity,
            requestedUnits: requestedPoolUnits,
          }).fits
        ) {
          return false;
        }
        if (showGuestCount && selectedOption) {
          return totalGuests >= selectedOption.minTicketsPerOrder && totalGuests <= selectedOption.maxTicketsPerOrder;
        }
        return true;
      }
      case "tickets": {
        const totalTickets = ticketSelections.reduce((sum, s) => sum + s.quantity, 0);
        if (totalTickets === 0) return false;
        // A selection can outlive the slot it was made against: choose a
        // roomier time, take 2, go back and choose a tighter one, and the
        // quantity stays while its cap drops. The stepper stops you SETTING
        // more and the sold-out path covers a cap of zero, but a cap of 1
        // under a held 2 is neither of those.
        //
        // Measured per POOL, not per ticket: two tickets drawing on one pool
        // can each sit inside their own cap while jointly overdrawing it.
        if (
          poolSelectionsExceedCaps({
            ticketTypes: v2.ticketTypes,
            selections: ticketSelections,
            capsByTicketTypeId: poolMaxTicketsByTicketTypeId,
          })
        ) {
          return false;
        }
        const apptMin = course?.appointmentScheduleSettings?.minTicketsPerOrder;
        const apptMax = course?.appointmentScheduleSettings?.maxTicketsPerOrder;
        if (apptMin && totalTickets < apptMin) return false;
        if (apptMax && totalTickets > apptMax) return false;
        return v2.constraintErrors.length === 0;
      }
      case "addons":
        return base.addOnGroupsValid;
      case "participants":
        return base.isParticipantsValid;
      case "userform":
        return base.isUserFormValid;
      case "personal": {
        const guest = base.personalDetails.guests[0];
        if (!guest) return false;
        const hasValidGuests = guestErrors.every(
          (errors) => !hasGuestErrors(errors),
        );

        // Check T&C acceptance if mandatoryTickBox is required
        const hasAcceptedTerms = mandatoryTickBox
          ? guest.mandatoryTickBox === true
          : true;

        return hasValidGuests && hasAcceptedTerms;
      }
      case "basketChoice":
        return true;
      default:
        return false;
    }
  }, [
    step,
    selectedDate,
    selectedTime,
    selectedOptionIndex,
    selectedSlotPoolMaxTickets,
    selectedSlotPoolFreeUnits,
    requestedPoolQuantity,
    requestedPoolUnits,
    poolMaxTicketsByTicketTypeId,
    v2.ticketTypes,
    base,
    mandatoryTickBox,
    guestErrors,
    ticketSelections,
    v2.constraintErrors,
  ]);

  // Get validation message for disabled button
  const getValidationMessage = useCallback((): string | undefined => {
    switch (step) {
      case "timeslot":
        if (!selectedDate) return "Please select a date";
        if (selectedTime === null) return "Please select a time slot";
        if (selectedOptionIndex === null) return "Please select an option";
        if (
          !slotPoolFit({
            poolMaxTickets: selectedSlotPoolMaxTickets,
            poolFreeUnits: selectedSlotPoolFreeUnits,
            requestedQuantity: requestedPoolQuantity,
            requestedUnits: requestedPoolUnits,
          }).fits
        ) {
          return "This time doesn't have enough available for the quantity you chose";
        }
        if (showGuestCount && selectedOption && totalGuests < selectedOption.minTicketsPerOrder)
          return `Minimum ${selectedOption.minTicketsPerOrder} guests required`;
        return undefined;
      case "tickets": {
        // Every pooled ticket at zero for this slot. The slot itself is NOT
        // dropped from the grid — a ticket with no pool could still book it,
        // and hiding the time from those is the failure mergePoolCapsIntoSlots
        // exists to avoid — so the customer can legitimately arrive here with
        // every row sold out. Without this they would be told to "select at
        // least one ticket", which describes the wrong problem entirely.
        if (
          poolMaxTicketsByTicketTypeId &&
          // EVERY ticket on the listing must be pool-bound and exhausted. The
          // map holds only pooled tickets, so "all entries are 0" says nothing
          // about a ticket with no pool — and the comment above is that such a
          // ticket is exactly why the slot is still offered. Claiming there is
          // no equipment while a spectator ticket is bookable would contradict
          // the reason the customer is standing here.
          v2.ticketTypes.length > 0 &&
          // Per TICKET, and by whether its POOL has room — not by an explicit
          // zero entry. A pooled ticket can be absent from the map (server
          // truncation, no window for this slot, or a lookup that failed and
          // was swallowed so the others still answered); it then renders
          // uncapped and selectable while poolSelectionsExceedCaps refuses any
          // quantity of it against a sibling's zero bound. Requiring `=== 0`
          // per visible ticket missed exactly that, leaving the customer
          // unable to proceed and told the wrong thing either way.
          //
          // `tt: any` is required TODAY, and the reason is a broken typecheck
          // config rather than anything about this code. useV2Pricing DOES
          // annotate its return `TicketType[]`, but @baluu/ui sets
          // moduleResolution NodeNext while every relative import here is
          // extensionless, so all of them resolve to `any` (that is most of the
          // package's 1078 errors) and `v2` with them — dropping the annotation
          // is a TS7006, as the identical `t.id` map a few hundred lines above
          // also is. RE-CHECK once the extension mismatch is fixed: at that
          // point this becomes real laziness and should go.
          v2.ticketTypes.every(
            (tt: any) =>
              poolMaxTicketsByTicketTypeId[tt.id] === 0 ||
              ticketIsUnbookableOnPool({
                ticket: tt,
                ticketTypes: v2.ticketTypes,
                capsByTicketTypeId: poolMaxTicketsByTicketTypeId,
              }),
          )
        ) {
          return "There's no equipment left at this time — try another time or date";
        }
        if (
          poolSelectionsExceedCaps({
            ticketTypes: v2.ticketTypes,
            selections: ticketSelections,
            capsByTicketTypeId: poolMaxTicketsByTicketTypeId,
          })
        ) {
          return "This time doesn't have enough available for the quantity you chose";
        }
        const totalTix = ticketSelections.reduce((sum, s) => sum + s.quantity, 0);
        if (totalTix === 0) return "Please select at least one ticket";
        const minGuests = course?.appointmentScheduleSettings?.minTicketsPerOrder;
        const maxGuests = course?.appointmentScheduleSettings?.maxTicketsPerOrder;
        if (minGuests && totalTix < minGuests) return `Minimum ${minGuests} guests required`;
        if (maxGuests && totalTix > maxGuests) return `Maximum ${maxGuests} guests allowed`;
        if (v2.constraintErrors.length > 0) return v2.constraintErrors[0].message;
        return undefined;
      }
      case "participants":
        if (!base.isParticipantsValid) return "Please add at least one attendee";
        return undefined;
      case "userform":
        if (!base.isUserFormValid) return "Please complete all required fields";
        return undefined;
      case "personal": {
        const guest = base.personalDetails.guests[0];
        if (!guest) return "Please fill in your details";
        const invalidGuestIndex = guestErrors.findIndex((errors) =>
          hasGuestErrors(errors),
        );
        if (invalidGuestIndex > 0)
          return `Please complete Guest ${invalidGuestIndex + 1} details`;
        if (!guest.firstName.trim()) return "First name is required";
        if (!guest.lastName.trim()) return "Last name is required";
        if (!guest.email.trim()) return "Email is required";
        if (!guest.phoneNumber.trim()) return "Phone number is required";
        if (mandatoryTickBox && !guest.mandatoryTickBox)
          return "Please accept the terms and conditions";
        return undefined;
      }
      default:
        return undefined;
    }
  }, [
    step,
    selectedDate,
    selectedTime,
    selectedOptionIndex,
    selectedSlotPoolMaxTickets,
    selectedSlotPoolFreeUnits,
    requestedPoolQuantity,
    requestedPoolUnits,
    poolMaxTicketsByTicketTypeId,
    v2.ticketTypes,
    base,
    mandatoryTickBox,
    guestErrors,
  ]);

  // Handle adding the item to basket
  const handleAddToBasket = useCallback(() => {
    if (!onAddToBasket || selectedTime === null || !selectedDate) return;
    if (!isV2 && !selectedOption) return;

    const allAddOnsForBasket = [...addOns, ...(addOnGroups || []).flatMap((g: any) => g.addOns)];
    const addOnSelections = base.addOnSelections
      .filter((sel) => sel.quantity > 0)
      .map((sel) => {
        const addOn = allAddOnsForBasket.find((a: any) => a.id === sel.addOnId);
        return {
          addOnId: sel.addOnId,
          addOnName: addOn?.name || "Add-on",
          quantity: sel.quantity,
          price: addOn?.price || 0,
          groupId: sel.groupId || null,
          groupName: sel.groupName || null,
        };
      });

    const hours = Math.floor(selectedTime / 60);
    const minutes = selectedTime % 60;
    const courseTimezone = timezone || course.timezone || "Europe/London";
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")} ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
    const sessionDate = moment.tz(dateStr, courseTimezone).toISOString();

    const basketTicketSelections = isV2
      ? ticketSelections
          .filter((sel) => sel.quantity > 0)
          .map((sel) => {
            const tt = ticketTypesV2.find((t: any) => t.id === sel.ticketTypeId);
            const v2Result = v2.pricingResults.find((r) => r.ticketTypeId === sel.ticketTypeId);
            const lineTotal = v2Result?.lineTotal;
            return {
              ticketTypeId: sel.ticketTypeId,
              ticketTypeName: tt?.name || "",
              quantity: sel.quantity,
              price: tt?.price || 0,
              lineTotal,
              numberOfGuestsPerTicket: 1,
              typeCharge: tt?.chargeType,
              bookingPolicy: tt?.bookingPolicy,
            };
          })
      : [{
          ticketTypeId: selectedOption!.id,
          ticketTypeName: selectedOption!.name,
          quantity: ticketCount,
          price: selectedOption!.price,
          numberOfGuestsPerTicket: selectedOption!.numberOfGuestsPerTicket,
          typeCharge: selectedOption!.typeCharge,
          bookingPolicy: selectedOption!.bookingPolicy,
        }];

    // Same derivation as handleProceedToBasketCheckout. Without it the item
    // carried only `sessionDate` — a single instant — so the basket could show
    // when an appointment STARTS but never when it ends.
    // ⚠ No fabricated fallback. `selectedDurationMinutes` is null exactly when
    // the length is NOT knowable — two selected tickets of different lengths,
    // or slots that disagree — and `?? 60` there stamped a confident hour onto
    // the item. `formatBookingWindow` PREFERS a stored duration over
    // end-minus-start, so that 60 then asserted "09:30-10:30 (1h)" on every
    // basket and checkout surface while the server sized the booking from the
    // schedule slot. Omitting appointmentData instead makes those surfaces
    // fall back to the start alone, which is what checkout-calendar-visibility
    // already does for the same reason.
    const addStartMoment = moment.tz(dateStr, courseTimezone);
    const addEndMoment =
      selectedDurationMinutes != null
        ? addStartMoment.clone().add(selectedDurationMinutes, "minutes")
        : null;

    onAddToBasket({
      type: "booking",
      bookingType: isGroupSchedule ? "appointment" : "appointment",
      courseId: course._id || course.id,
      courseName: course.title,
      sessionDate,
      ...(addEndMoment && selectedDurationMinutes != null
        ? {
            appointmentData: {
              startTime: addStartMoment.toISOString(),
              endTime: addEndMoment.toISOString(),
              duration: selectedDurationMinutes,
            },
          }
        : {}),
      ticketSelections: basketTicketSelections,
      addOns: addOnSelections.length > 0 ? addOnSelections : undefined,
      userInputs: base.userFormValues?.length ? base.userFormValues : undefined,
      participants: showParticipantsStep
        ? participantsToPayload(base.participantsValue)
        : undefined,
      price: total,
      currency,
      // Travels with the item so every basket surface renders the window on
      // the LISTING's clock -- two of them have no school config to read it
      // from. See formatBookingWindow.
      timezone: courseTimezone,
      imageUrl: course.images?.[0]?.url,
      isOnline: course.isOnline,
      guestDetails: {
        firstName: base.personalDetails.guests[0]?.firstName || "",
        lastName: base.personalDetails.guests[0]?.lastName || "",
        email: base.personalDetails.guests[0]?.email || "",
        phone: base.personalDetails.guests[0]?.phoneNumber || "",
      },
      checkoutGuestsInformation: base.personalDetails.guests.map((g: any) => ({
        firstName: g.firstName || "",
        lastName: g.lastName || "",
        email: g.email || "",
        phoneNumber: g.phoneNumber || "",
        marketingOptIn: g.marketingOptIn || false,
        mandatoryTickBox: g.mandatoryTickBox,
      })),
    });
    // showParticipantsStep / participantsValue: without them the callback sent
    // the attendee list from before the participants step (Oct 2026).
  }, [onAddToBasket, selectedOption, selectedTime, selectedDate, base.addOnSelections, base.personalDetails, base.userFormValues, showParticipantsStep, base.participantsValue, addOns, addOnGroups, course, total, currency, timezone, isV2, ticketSelections, ticketTypesV2, ticketCount, isGroupSchedule, v2.pricingResults, selectedDurationMinutes]);

  const handleProceedToBasketCheckout = useCallback(() => {
    if (selectedTime === null || !selectedDate) return;
    if (!isV2 && !selectedOption) return;

    const allAddOnsForCheckout = [...addOns, ...(addOnGroups || []).flatMap((g: any) => g.addOns)];
    const addOnSelections = base.addOnSelections
      .filter((sel) => sel.quantity > 0)
      .map((sel) => {
        const addOn = allAddOnsForCheckout.find((a: any) => a.id === sel.addOnId);
        return {
          addOnId: sel.addOnId,
          addOnName: addOn?.name || "Add-on",
          quantity: sel.quantity,
          price: addOn?.price || 0,
          groupId: sel.groupId || null,
          groupName: sel.groupName || null,
        };
      });

    const hours = Math.floor(selectedTime / 60);
    const minutes = selectedTime % 60;
    const courseTimezone = timezone || course.timezone || "Europe/London";
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")} ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
    const sessionDate = moment.tz(dateStr, courseTimezone).toISOString();

    // See handleAddToBasket — no fabricated fallback.
    const duration = selectedDurationMinutes;
    const startMoment = moment.tz(dateStr, courseTimezone);

    const checkoutTicketSelections = isV2
      ? ticketSelections
          .filter((sel) => sel.quantity > 0)
          .map((sel) => {
            const tt = ticketTypesV2.find((t: any) => t.id === sel.ticketTypeId);
            const v2Result = v2.pricingResults.find((r) => r.ticketTypeId === sel.ticketTypeId);
            const lineTotal = v2Result?.lineTotal;
            return {
              ticketTypeId: sel.ticketTypeId,
              ticketTypeName: tt?.name || "",
              quantity: sel.quantity,
              price: tt?.price || 0,
              lineTotal,
              numberOfGuestsPerTicket: 1,
              typeCharge: tt?.chargeType,
              bookingPolicy: tt?.bookingPolicy,
            };
          })
      : [{
          ticketTypeId: selectedOption!.id,
          ticketTypeName: selectedOption!.name,
          quantity: ticketCount,
          price: selectedOption!.price,
          numberOfGuestsPerTicket: selectedOption!.numberOfGuestsPerTicket,
          typeCharge: selectedOption!.typeCharge,
          bookingPolicy: selectedOption!.bookingPolicy,
        }];

    onProceedToBasketCheckout({
      type: "booking",
      bookingType: "appointment",
      courseId: course._id || course.id,
      courseName: course.title,
      sessionDate,
      ticketSelections: checkoutTicketSelections,
      addOns: addOnSelections.length > 0 ? addOnSelections : undefined,
      userInputs: base.userFormValues?.length ? base.userFormValues : undefined,
      participants: showParticipantsStep
        ? participantsToPayload(base.participantsValue)
        : undefined,
      ...(duration != null
        ? {
            appointmentData: {
              startTime: startMoment.toISOString(),
              endTime: startMoment.clone().add(duration, "minutes").toISOString(),
              duration,
            },
          }
        : {}),
      price: total,
      currency,
      // Travels with the item so every basket surface renders the window on
      // the LISTING's clock -- two of them have no school config to read it
      // from. See formatBookingWindow.
      timezone: courseTimezone,
      imageUrl: course.images?.[0]?.url,
      isOnline: course.isOnline,
      guestDetails: {
        firstName: base.personalDetails.guests[0]?.firstName || "",
        lastName: base.personalDetails.guests[0]?.lastName || "",
        email: base.personalDetails.guests[0]?.email || "",
        phone: base.personalDetails.guests[0]?.phoneNumber || "",
      },
      checkoutGuestsInformation: base.personalDetails.guests.map((g: any) => ({
        firstName: g.firstName || "",
        lastName: g.lastName || "",
        email: g.email || "",
        phoneNumber: g.phoneNumber || "",
        marketingOptIn: g.marketingOptIn || false,
        mandatoryTickBox: g.mandatoryTickBox,
      })),
    });
  }, [onProceedToBasketCheckout, selectedOption, selectedTime, selectedDate, base.addOnSelections, base.personalDetails, base.userFormValues, showParticipantsStep, base.participantsValue, addOns, addOnGroups, course, total, currency, timezone, isV2, ticketSelections, ticketTypesV2, ticketCount, v2.pricingResults, selectedDurationMinutes]);

  // Format selected time for display
  /**
   * The selected date and start, on the listing's clock.
   *
   * ⚠ Deliberately NO end time here. Five review rounds each found a different
   * way for a mid-flow duration to be wrong — an arbitrary slot at a shared
   * start, a branch made dead by the V2 auto-select, the most expensive of
   * several selected tickets, a fabricated 60 — because a duration cannot be
   * resolved from what this step knows. `durationMap` also pins a ticket to
   * ONE length across every schedule slot, while the server resolves it per
   * slot per date, so a ticket that runs 30m on weekdays and 60m on Saturday
   * disagrees here whatever the lookup order.
   *
   * The slot grid already shows the real window for the time being hovered
   * (it reads each slot's own `endMinutes`), and the basket and checkout show
   * it once a ticket is definitively chosen. This label stops competing with
   * both.
   */
  const selectedWindowParts = useMemo(() => {
    if (!selectedDate || selectedTime === null) return null;
    const tz = timezone || course.timezone || "Europe/London";
    const dayStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
    const hh = String(Math.floor(selectedTime / 60)).padStart(2, "0");
    const mm = String(selectedTime % 60).padStart(2, "0");
    const start = moment.tz(`${dayStr} ${hh}:${mm}:00`, tz);
    if (!start.isValid()) return null;
    // ⚠ ADDITIVE ONLY. `selectedDurationMinutes` is null exactly when the length
    // is not knowable — the resolver refuses to guess — and then `end` and
    // `length` stay undefined and both labels below render precisely what they
    // rendered before this: the day and the start, nothing invented.
    //
    // The END is a real instant (`add` on the moment), matching how
    // `appointmentData.endTime` is stamped, and it carries its day through
    // `formatWindowEnd` when it crosses midnight.
    const end =
      selectedDurationMinutes != null
        ? start.clone().add(selectedDurationMinutes, "minutes")
        : null;
    return {
      dayLong: start.format("ddd, D MMMM YYYY"),
      dayShort: start.format("ddd D MMM"),
      end: end ? formatWindowEnd(start, end) : undefined,
      length:
        selectedDurationMinutes != null
          ? (formatBookingDuration(selectedDurationMinutes) ?? undefined)
          : undefined,
      // ⚠ Straight from the minutes, NOT `start.format("HH:mm")`, and the slot
      // grid does the same (`minutesToTime(slot.startMinutes)`). On a
      // spring-forward day a start in the missing hour has no wall clock, and
      // moment silently resolves it an hour on — so the round trip would print
      // 02:30 in the footer beside a button the customer had just tapped
      // reading 01:30. Two labels for one choice must not disagree.
      start: `${hh}:${mm}`,
    };
  }, [selectedDate, selectedTime, selectedDurationMinutes, timezone, course.timezone]);

  /** "Thu, 18 June 2026, 09:30–10:30 (1h)" — summary panel and basket choice
   *  step. Falls back to the day and start alone when the length is unknown. */
  const formattedTime = useMemo(() => {
    const w = selectedWindowParts;
    if (!w) return undefined;
    return `${w.dayLong}, ${w.start}${w.end ? `–${w.end}` : ""}${w.length ? ` (${w.length})` : ""}`;
  }, [selectedWindowParts]);

  /**
   * The compact footer value. Keeps a short day: between sm and lg this bar is
   * the only summary on screen, since CheckoutSummaryCard sits in the sidebar
   * which is hidden below lg.
   */
  const formattedWindowShort = useMemo(() => {
    const w = selectedWindowParts;
    if (!w) return undefined;
    return `${w.dayShort}, ${w.start}${w.end ? `–${w.end}` : ""}${w.length ? ` (${w.length})` : ""}`;
  }, [selectedWindowParts]);

  // Build ticket items for summary
  const ticketItems = useMemo(() => {
    if (isV2) {
      return ticketSelections
        .filter((s) => s.quantity > 0)
        .map((sel) => {
          const tt = v2.ticketTypes.find((t) => t.id === sel.ticketTypeId);
          const pricingResult = v2.pricingResults.find((r) => r.ticketTypeId === sel.ticketTypeId);
          const lineTotal = bundleID ? 0 : pricingResult?.lineTotal;
          // A V2 `chargeType:"deposit"` ticket carries its split on the pricing
          // result (per-unit figures pre-scaled by the hook), not the legacy
          // `ticketTypeGroup:"deposit"` marker.
          const isV2Deposit =
            !bundleID &&
            pricingResult?.chargeType === "deposit" &&
            pricingResult?.depositAmount != null;
          return {
            name: bundleID ? `${tt?.name ?? "Ticket"} (Bundle)` : (tt?.name ?? "Ticket"),
            price: bundleID
              ? 0
              : isV2Deposit
                ? (pricingResult?.depositUnit ?? 0)
                : (pricingResult?.result.finalPrice ?? tt?.price ?? 0),
            fullPrice: isV2Deposit ? (pricingResult?.fullUnit ?? undefined) : undefined,
            count: sel.quantity,
            lineTotal,
            isDeposit: isV2Deposit,
            depositNowTotal: isV2Deposit
              ? (pricingResult?.depositLinePence ?? undefined)
              : undefined,
            depositContractTotal: isV2Deposit
              ? (pricingResult?.depositContractPence ?? undefined)
              : undefined,
            remainderCollection: isV2Deposit ? pricingResult?.remainderCollection : undefined,
            balanceDueDate: isV2Deposit ? pricingResult?.balanceDueDate : undefined,
            instalmentPreview: isV2Deposit
              ? pricingResult?.instalmentPreview
              : undefined,
            instalmentDiscountShares: isV2Deposit
              ? (v2.instalmentDiscountShares?.get(sel.ticketTypeId) ?? null)
              : undefined,
          };
        });
    }
    if (!selectedOption) return [];
    return [{
      name: bundleID ? `${selectedOption.name} (Bundle)` : selectedOption.name,
      price: bundleID ? 0 : selectedOption.price,
      count: ticketCount,
    }];
  }, [isV2, ticketSelections, v2.ticketTypes, v2.pricingResults, v2.instalmentDiscountShares, selectedOption, bundleID, ticketCount]);

  const allAddOnsForItems = [...addOns, ...(addOnGroups || []).flatMap((g: any) => g.addOns)];
  const addOnItems = base.addOnSelections
    .map((sel) => {
      const addOn = allAddOnsForItems.find((a: any) => a.id === sel.addOnId);
      if (!addOn || sel.quantity === 0) return null;
      return {
        name: addOn.name,
        price: addOn.price,
        count: sel.quantity,
      };
    })
    .filter(Boolean) as { name: string; price: number; count: number }[];

  return (
    <>
      <CheckoutPanel>
        <CheckoutPanel.Header
          title={course.title}
          subtitle={subtitle ?? "Appointment Booking"}
          onClose={onClose}
        />

        <CheckoutPanel.Content isEmbedMode={isEmbedMode}>
          <CheckoutPanel.Main>
            {/* Step Navigation */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              {visibleSteps.map((stepName, index) => (
                <React.Fragment key={stepName}>
                  {index > 0 && (
                    <span className="text-muted-foreground/70">/</span>
                  )}
                  <button
                    onClick={() =>
                      index < currentStepIndex && goToStep(stepName)
                    }
                    disabled={index > currentStepIndex}
                    className={`capitalize ${
                      index === currentStepIndex
                        ? "text-primary font-medium"
                        : index < currentStepIndex
                          ? "text-muted-foreground hover:text-primary cursor-pointer"
                          : "text-muted-foreground/70"
                    }`}
                  >
                    {stepName === "timeslot" ? "Time" : stepName === "basketChoice" ? "Basket" : stepName}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Time Slot Step */}
            {step === "timeslot" && (
              <>
                <AppointmentTimeSlotStep
                  isGroupSchedule={isGroupSchedule}
                  filteredForTicketTypeId={
                    isVariableDuration ? slotsFetchedForTicketTypeId : null
                  }
                  course={course}
                  availableDates={availableDates}
                  pricingOptions={pricingOptions}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  selectedOption={selectedOptionIndex}
                  onDateChange={setSelectedDate}
                  onTimeChange={setSelectedTime}
                  onOptionChange={setSelectedOptionIndex}
                  onMonthChange={handleMonthChange}
                  currentMonth={currentMonth}
                  isLoading={base.isLoading}
                  currency={currency}
                  timezone={timezone || course.timezone}
                  nextAvailableDateInfo={nextAvailableDateInfo}
                  isSearchingNextAvailable={isSearchingNextAvailable}
                  hidePricingOptions={isV2}
                  requestedPoolQuantity={requestedPoolQuantity}
                  requestedPoolUnits={requestedPoolUnits}
                  locationsById={locationsById}
                  locationOptions={locationOptions}
                  locationFilter={locationFilter}
                  onChangeLocationFilter={handleLocationFilterChange}
                  hasMultipleLocations={scheduleHasMultipleLocations}
                  locationSelectionMode={locationSelectionMode}
                />

                {showGuestCount && selectedOption && (
                  <div className="mt-6 rounded-lg border bg-card p-4">
                    <h3 className="text-base font-semibold mb-1">Number of Guests</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Minimum {selectedOption.minTicketsPerOrder} — Maximum {selectedOption.maxTicketsPerOrder} guests
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="flex h-9 w-9 items-center justify-center rounded-md border text-lg font-medium transition-colors hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                        onClick={() => setGroupGuestCount((prev) => Math.max(selectedOption.minTicketsPerOrder, (prev ?? selectedOption.minTicketsPerOrder) - 1))}
                        disabled={totalGuests <= selectedOption.minTicketsPerOrder}
                        aria-label="Decrease guest count"
                      >
                        −
                      </button>
                      <span className="min-w-12 text-center text-lg font-semibold tabular-nums">
                        {totalGuests}
                      </span>
                      <button
                        type="button"
                        className="flex h-9 w-9 items-center justify-center rounded-md border text-lg font-medium transition-colors hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                        onClick={() => setGroupGuestCount((prev) => Math.min(selectedOption.maxTicketsPerOrder, (prev ?? selectedOption.minTicketsPerOrder) + 1))}
                        disabled={totalGuests >= selectedOption.maxTicketsPerOrder}
                        aria-label="Increase guest count"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Tickets Step (V2 only) — uses the same shared component as regular/term checkout */}
            {step === "tickets" && isV2 && (
              <TicketSelectionStepV2
                ticketTypes={v2.ticketTypes}
                selections={ticketSelections}
                onSelectionChange={updateTicketSelection}
                currency={currency}
                totalAvailability={
                  selectedSlotPublicInfo?.isPublic &&
                  selectedSlotPublicInfo.spotsRemaining !== null
                    ? selectedSlotPublicInfo.spotsRemaining
                    : course?.appointmentScheduleSettings?.maxTicketsPerOrder
                }
                hidePrice={!!effectiveBundleID}
                bundleMaxTickets={
                  effectiveBundleID && bundle
                    ? bundle.totalSessions - (bundle.usedSessions || 0)
                    : undefined
                }
                constraintErrors={v2.constraintErrors}
                cartPricingRules={cartPricingRules}
                showBundleMembershipHelp={showBundleMembershipHelp}
                poolMaxTicketsByTicketTypeId={poolMaxTicketsByTicketTypeId}
              />
            )}

            {/* Add-ons Step */}
            {step === "addons" && (
              <AddOnsStep
                addOns={addOns}
                groups={addOnGroups}
                selections={base.addOnSelections}
                onSelectionChange={base.updateAddOnSelection}
                currency={currency}
              />
            )}

            {/* Participants Step */}
            {step === "participants" && (
              <ParticipantsStep
                participantLabel={participantLabel}
                fields={participantFields}
                existing={existingParticipants}
                maxParticipants={Math.max(1, totalGuests)}
                value={base.participantsValue}
                onChange={base.setParticipantsValue}
                onValidationChange={base.setIsParticipantsValid}
                buyerIsAttendee={buyerIsAttendee}
                allowUnnamed={allowUnnamedAttendees}
                buyerSeededRef={base.buyerAttendeeSeededRef}
                selfSeed={selfSeed}
                onSelfAnswersChange={onSelfAnswersChange}
                buyer={{
                  // The typed personal-details step wins; the signed-in
                  // customer's own record fills in when this flow skips it
                  // (basket-enabled listings collect the buyer downstream).
                  firstName: base.personalDetails.guests[0]?.firstName || customer?.firstName,
                  lastName: base.personalDetails.guests[0]?.lastName || customer?.lastName,
                  email: base.personalDetails.guests[0]?.email || customer?.email,
                  phone: base.personalDetails.guests[0]?.phoneNumber || customer?.phone,
                }}
              />
            )}

            {/* User Form Step */}
            {step === "userform" && (
              <UserFormStep
                questions={course.userQuestionForm?.questions || []}
                totalGuests={totalGuests}
                participantsEnabled={showParticipantsStep}
                values={base.userFormValues}
                onChange={base.setUserFormValues}
                onValidationChange={base.setIsUserFormValid}
                mode={course.userQuestionForm?.mode || "all"}
              />
            )}

            {/* Personal Details Step */}
            {step === "personal" && (
              <PersonalDetailsStep
                values={{
                  firstName: base.personalDetails.guests[0]?.firstName || "",
                  lastName: base.personalDetails.guests[0]?.lastName || "",
                  email: base.personalDetails.guests[0]?.email || "",
                  phone: base.personalDetails.guests[0]?.phoneNumber || "",
                  marketingOptIn:
                    base.personalDetails.guests[0]?.marketingOptIn || false,
                  mandatoryTickBox:
                    base.personalDetails.guests[0]?.mandatoryTickBox || false,
                }}
                errors={{
                  firstName: guestErrors[0]?.firstName,
                  lastName: guestErrors[0]?.lastName,
                  email: guestErrors[0]?.email,
                  phone: guestErrors[0]?.phoneNumber,
                }}
                touched={{
                  firstName: guestTouched[0]?.firstName,
                  lastName: guestTouched[0]?.lastName,
                  email: guestTouched[0]?.email,
                  phone: guestTouched[0]?.phoneNumber,
                }}
                onChange={(field, value) => {
                  const fieldMap: Record<string, string> = {
                    firstName: "firstName",
                    lastName: "lastName",
                    email: "email",
                    phone: "phoneNumber",
                    marketingOptIn: "marketingOptIn",
                    mandatoryTickBox: "mandatoryTickBox",
                  };
                  base.updatePersonalDetails(0, fieldMap[field] as any, value);
                }}
                onBlur={(field) => {
                  const fieldMap: Record<string, string> = {
                    firstName: "firstName",
                    lastName: "lastName",
                    email: "email",
                    phone: "phoneNumber",
                  };
                  handleGuestBlur(0, fieldMap[field] || field);
                }}
                mandatoryTickBox={mandatoryTickBox}
                showMarketingOptIn={school?.isNewsletterSignupEnabled ?? true}
                businessName={school?.name || ""}
                customMarketingOptInLabel={school?.customNewsletterSignupText}
                schoolID={school?._id || ""}
                businessCountry={school?.businessCountry}
              />
            )}

            {/* Basket Choice Step */}
            {step === "basketChoice" && (
              <BasketChoiceStep
                itemName={course.title}
                dateLabel={formattedTime}
                total={total}
                currency={currency}
                basketItemCount={basketItemCount}
                onAddToBasket={handleAddToBasket}
                onProceedToCheckout={handleProceedToBasketCheckout}
                directCheckout={directCheckout}
                basketConflict={basketConflict}
              />
            )}

          </CheckoutPanel.Main>

          <CheckoutPanel.Sidebar>
            <CheckoutSummaryCard
              title={course.title}
              thumbnail={course.images?.[0]?.url}
              description={formattedTime}
              isOnline={course.isOnline}
              locationLabel={sidebarLocationLabel}
              items={[...ticketItems, ...addOnItems]}
              total={total}
              currency={currency}
              discount={discountAmount}
              discountPercentage={discountPercentage}
              totalRemainingBalance={isV2 ? v2.totalRemainingBalance : 0}
              cancellationPolicyUrl={cancellationPolicyUrl}
              termsSettings={termsSettings}
              customTerms={customTerms}
              timezone={timezone}
              onOpenTermsDialog={onOpenTermsDialog}
              showBundleMembershipHelp={showBundleMembershipHelp}
            />
          </CheckoutPanel.Sidebar>
        </CheckoutPanel.Content>
      </CheckoutPanel>

      {/* Footer */}
      <CheckoutFooter
        label="Time Selected"
        value={formattedWindowShort}
        total={total}
        currency={currency}
        showBack={!isFirstStep}
        onBack={goToPreviousStep}
        showCancel={!!onClose}
        onCancel={onClose}
        showContinue={step !== "basketChoice"}
        onContinue={goToNextStep}
        continueText="Continue"
        continueDisabled={!canContinue()}
        validationMessage={getValidationMessage()}
        isEmbedMode={isEmbedMode}
      />
    </>
  );
}
